"use client";

import { FormattedMessage, useIntl } from "react-intl";
import { FaBuilding, FaUserCircle } from "react-icons/fa";
import { FaCheck } from "react-icons/fa6";
import { useLocale } from "@/app/providers/IntlProvider";
import { BAND_WIDTH, SECTION_GUTTER } from "../widths";
import { LiveStage } from "../Story/LiveStage";

/**
 * The two spaces of a Flots account, as the product enforces them (ADR 0033): the personal
 * space the organisation never sees, and the company space the team shares. Copy in
 * `trust.<id>.title|desc`; icons as on the profile fork.
 */
const SPACES: { id: "personal" | "company"; Icon: typeof FaUserCircle }[] = [
  { id: "personal", Icon: FaUserCircle },
  { id: "company", Icon: FaBuilding },
];

/** The facts line under the two spaces: hosting, never sold, no training. */
const FACTS = ["trust.hosted", "trust.neverSold", "trust.noTraining"];

/**
 * Pillar 3 of the sales argument, after the profile fork: your data stays yours. A brand-blue
 * band that puts data sovereignty first (what you put in Flots belongs to you), then draws the
 * line between the personal space and the company space in two short columns, and closes on
 * one line of facts, beside the European-hosting animation (the quote and the shop photos
 * locked in a shield). Text is pure white: on blue-main only full white clears WCAG AA (4.6:1).
 */
export function TrustSection() {
  const intl = useIntl();
  const { locale } = useLocale();
  return (
    <section id="confiance" aria-labelledby="confiance-title" className={`scroll-mt-24 pb-14 sm:pb-24 ${SECTION_GUTTER}`}>
      {/* Same edges as the story panel and the profile band. */}
      <div className={`mx-auto grid items-center gap-8 overflow-hidden rounded-[32px] bg-blue-main p-6 [--focus-ring:white] sm:rounded-[40px] sm:p-10 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] lg:gap-x-12 lg:gap-y-8 xl:p-14 min-[1920px]:gap-x-20 min-[1920px]:p-20 ${BAND_WIDTH}`}>
        <div>
          <h2
            id="confiance-title"
            className="text-balance text-[2rem] font-bold leading-[1.1] tracking-[-0.02em] text-white sm:text-5xl lg:text-4xl xl:text-5xl"
          >
            <FormattedMessage id="story.group.trust" />
            {/* The brand full stop, lightened so it still reads on the blue band. */}
            <span className="text-blue-sec">.</span>
          </h2>
          <p className="mt-4 max-w-xl text-lg leading-relaxed text-white">
            <FormattedMessage id="trust.lead" />
          </p>

          <div className="mt-8 grid gap-6 sm:grid-cols-2 sm:gap-8">
            {SPACES.map(({ id, Icon }) => (
              <div key={id}>
                <h3 className="flex items-center gap-2 text-base font-bold text-white">
                  <Icon aria-hidden="true" className="shrink-0 text-lg text-blue-sec" />
                  <FormattedMessage id={`trust.${id}.title`} />
                </h3>
                <p className="mt-1.5 text-[15px] leading-relaxed text-white">
                  <FormattedMessage id={`trust.${id}.desc`} />
                </p>
              </div>
            ))}
          </div>

        </div>

        <LiveStage
          name="europe"
          locale={locale}
          label={intl.formatMessage({ id: "story.europe.title" })}
          replayLabel={intl.formatMessage({ id: "profile.replay" }, { title: intl.formatMessage({ id: "story.europe.title" }) })}
          className="aspect-[5/4] rounded-[28px] bg-blue-ulight focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-white"
        />

        {/* Spans the whole band on desktop so the three facts hold on one line. */}
        <ul className="flex flex-wrap gap-x-6 gap-y-2 text-sm font-semibold text-white lg:col-span-2">
          {FACTS.map((key) => (
            <li key={key} className="flex items-center gap-2">
              <FaCheck aria-hidden="true" className="shrink-0 text-xs text-blue-sec" />
              <FormattedMessage id={key} />
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
